import { captureEvent, captureException, Severity, withScope } from '@sentry/browser';
import React, { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { appClient } from 'src/config/apollo';
import { Dispatch } from 'src/config/rootStore';
import { Assessment } from 'src/modules/assessments/components/Assessment';
import {
    AssessmentType,
    FlowType,
    MappedAnswers,
} from 'src/modules/assessments/models/assessments';
import {
    getAssessments,
    getWho5Score,
} from 'src/modules/assessments/selectors/assessmentsSelectors';
import { getMostRecentCareRecommendation } from 'src/modules/user/selectors/userSelectors';

import { useStartAssessmentFlowMutation } from '../documents/startAssessmentFlowMutation.generated';
import { CenteredSpinner } from 'src/shared/components/CenteredSpinner';

export interface Scores {
    previousWho5: number;
    [AssessmentType.Who5]: number;
    [AssessmentType.Phq2]?: number;
    [AssessmentType.Phq8]?: number;
    [AssessmentType.Phq9]?: number;
    [AssessmentType.Gad2]?: number;
    [AssessmentType.Gad7]?: number;
}
interface Props {
    onSubmit: (scores: Scores) => void;
    flowType: FlowType;
    startAssessmentFlowVariables?: Record<string, string>;
    hideEmploymentHealthQuestions?: boolean;
    showTopControls?: boolean;
    onlyWho5?: boolean;
}

export const SetOfAssessments: React.FC<Props> = ({
    onSubmit,
    flowType,
    showTopControls = false,
    startAssessmentFlowVariables = {},
}) => {
    const availableAssessments = useSelector(getAssessments);
    const who5Score = useSelector(getWho5Score);
    const careRecommendationUuid = useSelector(getMostRecentCareRecommendation)?.uuid;

    const isAssessmentAvailable = assessment =>
        Object.keys(availableAssessments).includes(assessment.toLowerCase());
    const logUnavailableAssessment = () =>
        withScope(scope => {
            scope.setExtra('Outcomes', 'Error getting assessment from server');
            captureException('The assessmentKey received from the server was not recognized');
        });

    const dispatch: Dispatch = useDispatch();

    const [startAssessmentFlow, { data, loading }] = useStartAssessmentFlowMutation({
        client: appClient,
        fetchPolicy: 'no-cache',
    });

    const [assessmentFlowUuid, setAssessmentFlowUUid] = useState('');
    const [assessmentKey, setAssessmentKey] = useState('');
    const isMounted = useRef(false);

    useEffect(() => {
        isMounted.current = true;
        startAssessmentFlow({
            variables: {
                flowType,
                careRecommendationUuid,
                ...startAssessmentFlowVariables,
            },
        })
            .then(response => {
                const uuid = response?.data?.startAssessmentFlow?.assessmentFlow?.uuid;

                if (!uuid) {
                    captureEvent({
                        message: 'We have not received correct assessment flow uuid in a response',
                        level: Severity.Error,
                        extra: { uuid },
                    });
                } else {
                    const nextAssessment = response?.data?.startAssessmentFlow?.nextAssessments[0];
                    if (nextAssessment) {
                        if (!isAssessmentAvailable(nextAssessment)) {
                            logUnavailableAssessment();
                        }
                        setAssessmentFlowUUid(uuid);
                        setAssessmentKey(nextAssessment.toLowerCase());
                        return;
                    }
                    setAssessmentFlowUUid(uuid);
                }
            })
            .catch(captureException);
        return () => {
            isMounted.current = false;
        };
    }, []);

    const submitAssessment = async (answers: MappedAnswers) => {
        const previousWho5 = who5Score;
        const { scores, nextAssessment } = await dispatch.assessments.submitAssessments({
            answers,
            assessment: {
                name: assessmentKey,
                assessmentFlowUuid,
            },
            careRecommendationUuid,
        });

        if (!isMounted.current) {
            return;
        }

        setAssessmentKey(nextAssessment.toLowerCase());

        const data = {
            previousWho5,
            ...scores,
        };

        if (!nextAssessment) {
            onSubmit(data as Scores);
            return;
        }

        if (!isAssessmentAvailable(nextAssessment)) {
            logUnavailableAssessment();
        }
    };

    if (loading || !data || assessmentKey === '') {
        return <CenteredSpinner />;
    }

    if (availableAssessments[assessmentKey]?.assessmentQuestions) {
        return (
            <Assessment
                assessmentFlowUuid={assessmentFlowUuid}
                key={assessmentKey}
                assessmentName={assessmentKey}
                questions={availableAssessments[assessmentKey].assessmentQuestions}
                onSubmit={submitAssessment}
                showTopControls={assessmentKey === 'who5' && showTopControls}
                hideProgressBar
            />
        );
    }

    // If we get here, there was an issue that was logged on line 107
    return null;
};
